import axios from './axios';

const API_URL = '/v1';

type ApiEnvelope<T> = {
  content: T;
  response_code?: string;
  message?: string;
};

type Identifier = number | string;
type ProjectAction = 'approve' | 'reject';
type BaselineSaveMode = 'draft' | 'final';
type RecordData = Record<string, unknown>;

type ProjectService = {
  getProjects: (categoryId?: Identifier | null) => Promise<unknown>;
  createProject: (data: RecordData) => Promise<unknown>;
  deleteProject: (id: Identifier) => Promise<unknown>;
  getProjectTree: (id: Identifier) => Promise<unknown>;
  getVendors: (search?: string) => Promise<unknown>;
  saveBaseline: (id: Identifier, data: RecordData, saveMode?: BaselineSaveMode) => Promise<unknown>;
  updateTaskProgress: (
    projectId: Identifier,
    taskId: Identifier,
    progress: number,
    notes?: string,
  ) => Promise<unknown>;
  assignTechnicians: (
    projectId: Identifier,
    taskId: Identifier,
    servicemanIds: Identifier[],
  ) => Promise<unknown>;
  addExpense: (projectId: Identifier, data: RecordData | FormData) => Promise<unknown>;
  toggleResourceUsed: (
    projectId: Identifier,
    resourceId: Identifier,
  ) => Promise<unknown>;
  updateManagerNote: (
    projectId: Identifier,
    note: string,
  ) => Promise<unknown>;
  getValidationQueue: (projectId: Identifier) => Promise<unknown>;
  validateTask: (
    projectId: Identifier,
    taskId: Identifier,
    action: ProjectAction,
    reason?: string,
    validationResults?: Record<string, boolean>,
  ) => Promise<unknown>;
};

const projectService: ProjectService = {
  async getProjects(categoryId = null) {
    const url = categoryId
      ? `${API_URL}/projects?category_id=${categoryId}`
      : `${API_URL}/projects`;
    const response = await axios.get<ApiEnvelope<unknown>>(url);
    return response.data.content;
  },

  async createProject(data) {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects`,
      data,
    );
    return response.data.content;
  },

  async deleteProject(id) {
    const response = await axios.delete<unknown>(`${API_URL}/projects/${id}`);
    return response.data;
  },

  async getProjectTree(id) {
    const response = await axios.get<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${id}/tree`,
    );
    return response.data.content;
  },

  async getVendors(search = '') {
    const response = await axios.get<ApiEnvelope<unknown>>(
      `${API_URL}/projects/vendors?search=${search}`,
    );
    return response.data.content;
  },

  async saveBaseline(id, data, saveMode = 'final') {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${id}/baseline`,
      {
        ...data,
        save_mode: saveMode,
      },
    );
    return response.data.content;
  },

  async updateTaskProgress(projectId, taskId, progress, notes = '') {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/tasks/${taskId}/progress`,
      {
        progress,
        notes,
      },
    );
    return response.data.content;
  },

  async assignTechnicians(projectId, taskId, servicemanIds) {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/tasks/${taskId}/assign`,
      {
        serviceman_ids: servicemanIds,
      },
    );
    return response.data.content;
  },

  async addExpense(projectId, data) {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/expenses`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.content;
  },

  async toggleResourceUsed(projectId, resourceId) {
    const response = await axios.patch<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/resources/${resourceId}/toggle-used`,
    );
    return response.data.content;
  },

  async updateManagerNote(projectId, note) {
    const response = await axios.patch<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/manager-note`,
      {
        manager_note: note,
      },
    );
    return response.data.content;
  },

  async getValidationQueue(projectId) {
    const response = await axios.get<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/validation-queue`,
    );
    return response.data.content;
  },

  async validateTask(projectId, taskId, action, reason = '', validationResults = {}) {
    const response = await axios.post<ApiEnvelope<unknown>>(
      `${API_URL}/projects/${projectId}/tasks/${taskId}/validate`,
      {
        action,
        rejection_reason: reason,
        validation_results: validationResults,
      },
    );
    return response.data.content;
  },
};

export default projectService;


